import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Clapperboard, CornerDownLeft, FileText, Folder, Image as ImageIcon, LayoutDashboard, Languages,
  Moon, Newspaper, Package, Plus, Search, Signpost,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useArticles, useServices } from '@/lib/queries';

/**
 * ⌘K palette: jump to any article, service or page by name; create; toggle
 * theme (§10.6). Content results come from the live query cache, so the thing
 * a marketer just edited is findable by its title in either language.
 */
interface Command {
  id: string;
  label: string;
  hint?: string;
  section: 'go' | 'create' | 'actions';
  icon: React.ReactNode;
  run: () => void;
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const { toggle } = useTheme();
  const { canManageWebsite } = useAuth();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Only fetch website content when the palette is actually open AND the user
  // may manage it. An SMM (Stories-only) would 403 on these, and a closed
  // palette needs nothing — so no /articles or /services request fires on the
  // Stories page (or any page) until ⌘K is pressed by someone who can use it.
  const wantContent = open && canManageWebsite;
  const articles = useArticles({ q: query || undefined, pageSize: 6 }, wantContent);
  const services = useServices(wantContent);

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => {
      navigate(to);
      onOpenChange(false);
    };

    // Website destinations an SMM has no route to are dropped from the palette
    // too — it must not offer to navigate somewhere the guard will bounce.
    const website: Command[] = canManageWebsite
      ? [
          { id: 'go-articles', label: t.nav.articles, section: 'go', icon: <Newspaper className="h-4 w-4" />, run: go('/articles') },
          { id: 'go-services', label: t.nav.services, section: 'go', icon: <Package className="h-4 w-4" />, run: go('/services') },
          { id: 'go-pages', label: t.nav.pages, section: 'go', icon: <FileText className="h-4 w-4" />, run: go('/pages') },
          { id: 'go-categories', label: t.nav.categories, section: 'go', icon: <Folder className="h-4 w-4" />, run: go('/categories') },
          { id: 'go-redirects', label: t.nav.redirects, section: 'go', icon: <Signpost className="h-4 w-4" />, run: go('/redirects') },
          // Murojaatlar (leads) hozircha yashirilgan.
          { id: 'new-article', label: t.articles.new, section: 'create', icon: <Plus className="h-4 w-4" />, run: go('/articles/new') },
          { id: 'new-service', label: t.services.new, section: 'create', icon: <Plus className="h-4 w-4" />, run: go('/services/new') },
        ]
      : [];

    const base: Command[] = [
      { id: 'go-overview', label: t.nav.overview, section: 'go', icon: <LayoutDashboard className="h-4 w-4" />, run: go('/') },
      { id: 'go-stories', label: t.nav.stories, section: 'go', icon: <Clapperboard className="h-4 w-4" />, run: go('/stories') },
      { id: 'go-media', label: t.nav.media, section: 'go', icon: <ImageIcon className="h-4 w-4" />, run: go('/media') },
      ...website,
      {
        id: 'theme', label: t.palette.toggleTheme, section: 'actions', icon: <Moon className="h-4 w-4" />,
        run: () => { toggle(); onOpenChange(false); },
      },
      {
        id: 'lang', label: t.palette.switchLang, hint: lang === 'uz' ? "O'zbekcha → Русский" : 'Русский → O‘zbekcha',
        section: 'actions', icon: <Languages className="h-4 w-4" />,
        run: () => { setLang(lang === 'uz' ? 'ru' : 'uz'); onOpenChange(false); },
      },
    ];

    const content: Command[] = [
      ...(articles.data?.items ?? []).map((article) => ({
        id: `article-${article.id}`,
        label: article.title[lang] ?? article.title.uz ?? article.title.ru ?? t.articles.untitled,
        hint: `${t.nav.articles} · /${article.slug}`,
        section: 'go' as const,
        icon: <Newspaper className="h-4 w-4" />,
        run: go(`/articles/${article.id}`),
      })),
      ...(services.data?.items ?? []).map((service) => ({
        id: `service-${service.id}`,
        label: service.title[lang] ?? service.title.uz ?? t.articles.untitled,
        hint: `${t.nav.services} · /${service.slug}`,
        section: 'go' as const,
        icon: <Package className="h-4 w-4" />,
        run: go(`/services/${service.id}`),
      })),
    ];

    return [...content, ...base];
  }, [t, lang, setLang, navigate, onOpenChange, toggle, articles.data, services.data, canManageWebsite]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return commands;
    return commands.filter(
      (command) =>
        command.label.toLowerCase().includes(needle) || command.hint?.toLowerCase().includes(needle),
    );
  }, [commands, query]);

  useEffect(() => setCursor(0), [query, open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const sections: Array<[Command['section'], string]> = [
    ['go', t.palette.sections.go],
    ['create', t.palette.sections.create],
    ['actions', t.palette.sections.actions],
  ];

  // A flat index across sections so ↑/↓ walks the rendered order.
  let flatIndex = -1;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink-900/45 backdrop-blur-[2px] data-[state=open]:animate-[fade-rise_160ms_var(--ease-out)]" />
        <DialogPrimitive.Content
          aria-label={t.palette.placeholder}
          className="fixed left-1/2 top-[12vh] z-50 w-[min(38rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-card border border-line bg-surface shadow-pop data-[state=open]:animate-pop-in"
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setCursor((c) => Math.min(c + 1, filtered.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              filtered[cursor]?.run();
            }
          }}
        >
          <DialogPrimitive.Title className="sr-only">{t.palette.placeholder}</DialogPrimitive.Title>
          <div className="flex items-center gap-3 border-b border-line px-4">
            <Search aria-hidden className="h-4 w-4 shrink-0 text-faint" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.palette.placeholder}
              className="h-14 w-full bg-transparent text-body text-strong outline-none placeholder:text-faint"
            />
          </div>

          <div ref={listRef} className="max-h-[22rem] overflow-y-auto p-2">
            {filtered.length === 0 && (
              <p className="px-3 py-8 text-center text-ui text-muted">{t.palette.noResults}</p>
            )}
            {sections.map(([section, label]) => {
              const items = filtered.filter((command) => command.section === section);
              if (items.length === 0) return null;
              return (
                <div key={section} className="mb-1">
                  <p className="eyebrow px-3 py-1.5">{label}</p>
                  {items.map((command) => {
                    flatIndex += 1;
                    const index = flatIndex;
                    return (
                      <button
                        key={command.id}
                        type="button"
                        onMouseEnter={() => setCursor(index)}
                        onClick={command.run}
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-3 rounded-control px-3 py-2 text-left',
                          'transition-ui duration-instant',
                          index === cursor ? 'bg-brand-500/10 text-strong' : 'text-muted hover:bg-raised',
                        )}
                      >
                        <span className="shrink-0 text-brand-500 dark:text-brand-300">{command.icon}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-ui text-strong">{command.label}</span>
                          {command.hint && (
                            <span className="block truncate font-mono text-[0.6875rem] text-faint">{command.hint}</span>
                          )}
                        </span>
                        {index === cursor && <CornerDownLeft aria-hidden className="h-3.5 w-3.5 shrink-0 text-faint" />}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
